import { has } from "shared/utils/has";
import { Template } from "types/landlord/verificationTemplate";
import { VerificationNames } from "shared/types";
import { tabTitle } from "./utils";
import { MAX_FEE_CENTS } from "./slices/AdditionalFeesSlice/constants";

export const onSaveValidation = {
  rules: (template: Template) => {
    if (template.recommendation_method === "rule_based") {
      if (
        template.auto_approve_delay_enabled &&
        (template.auto_approve_delay_value || 0) <= 0
      )
        return "Auto approve delay value must be greater than 0";

      const hasEmptyRuleSets = template.rule_criteria?.some(
        (criteria) => !criteria.rules.length
      );

      if (hasEmptyRuleSets) return "Empty rule sets are not allowed";

      const hasDeclineRule = template.rule_criteria?.some(
        (criteria) => criteria.type === "decline" && criteria.rules.length
      );
      const hasApproveRule = template.rule_criteria?.some(
        (criteria) => criteria.type === "approve" && criteria.rules.length
      );

      if (!hasDeclineRule || !hasApproveRule || !template.rule_criteria?.length)
        return "Template must have at least one approve and decline rule when using rule based recommendation";

      // Check if all rules have values
      const hasEmptyRule = template.rule_criteria.some((criteria) =>
        criteria.rules.some((rule) => !has(rule.value) || rule.value === "")
      );

      if (hasEmptyRule) return "All rules must have a value";
    }
  },
  criteria: (
    template: Template,
    setApplicantSettingsErrors: (errors: Record<string, string>) => void
  ) => {
    if (template.recommendation_method === "score_based") {
      const newErrors = {};

      Object.entries(
        template.screening_criteria?.applicant_settings || {}
      ).forEach(([key, entry]) => {
        if (entry.value !== 0 && !entry.value)
          newErrors[key] = "This value is required";
      });

      setApplicantSettingsErrors(newErrors);

      if (Object.keys(newErrors).length)
        return "All criteria must have a value";
    }
  },
  combinedIncomeVerificationMethods: (template: Template) => {
    for (const partnerType of ["applicant", "co_signer", "occupant"]) {
      const combinedIncomeSchema =
        template.verification_schemas[partnerType]?.[
          VerificationNames.CombinedIncome
        ];
      if (!combinedIncomeSchema) continue;

      // Income types that have verification methods
      for (const incomeType of ["employment", "self_employment", "other"]) {
        const config = combinedIncomeSchema[incomeType];
        if (!config) continue;

        // Ensure at least one verification method is enabled
        if (
          [
            "bank_allowed",
            "payroll_allowed",
            "paystub",
            "bank_statement",
            "w2"
          ].every((verificationMethodKey) => verificationMethodKey in config)
        ) {
          const isDocumentVerificationEnabled =
            config.paystub?.allowed ||
            config.bank_statement?.allowed ||
            config.w2?.allowed;
          const isAnyVerificationMethodEnabled =
            config.bank_allowed ||
            config.payroll_allowed ||
            isDocumentVerificationEnabled;

          if (!isAnyVerificationMethodEnabled)
            return `you need to select at least one verification method for the ${tabTitle[partnerType]} -> Income history ->  ${incomeType.replace("_", " ")}`;
        }
      }
    }

    return null;
  },
  additionalFees: (template: Template) => {
    const fees = template.template_fees || [];
    const enabledFees = fees.filter((f) => f.enabled);

    const hasEmptyFee = enabledFees.some((fee) => {
      return Number(fee.amount.cents) <= 0;
    });
    if (hasEmptyFee) return "All enabled additional fees must have a value";

    const hasFeeOverMaximum = enabledFees.some((fee) => {
      return fee.amount.cents > MAX_FEE_CENTS;
    });
    if (hasFeeOverMaximum)
      return `Additional fees cannot be greater than $${(MAX_FEE_CENTS / 100).toLocaleString()}`;
  }
};
